import React, { useState, useRef, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useDressesForSelect } from '../hooks/api/useDresses';
import { useDebounce } from '../utils/useDebounce';

interface Option {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  onChange?: (value: string | number, option: Option) => void;
  size?: 'sm' | 'md' | 'lg';
  allowClear?: boolean;
  searchPlaceholder?: string;
  branchId?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  defaultValue,
  placeholder = 'Search and select dress',
  disabled = false,
  className,
  onChange,
  size = 'md',
  allowClear = false,
  searchPlaceholder = 'Search by dress name, barcode, or color...',
  branchId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Debounce search term to avoid too many API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Fetch dresses with search
  const { data: dresses = [], isLoading, error } = useDressesForSelect(debouncedSearchTerm, 100, branchId);

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  // Convert dresses to options
  const options: Option[] = dresses.map(dress => ({
    value: dress.id,
    label: `${dress.name} (${dress.barcode}) - $${dress.rental_price}`,
    disabled: false
  }));

  const selectedOption = options.find(option => option.value === selectedValue);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = useCallback((option: Option) => {
    if (option.disabled) return;
    
    setSelectedValue(option.value);
    setIsOpen(false);
    setSearchTerm('');
    onChange?.(option.value, option);
  }, [onChange]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValue(undefined);
    onChange?.('' as any, { value: '', label: '' });
  }, [onChange]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }, [disabled, isOpen]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  return (
    <div ref={selectRef} className={clsx('relative', className)}>
      <div
        className={clsx(
          'flex items-center justify-between w-full border rounded-lg bg-white cursor-pointer transition-colors',
          sizeClasses[size],
          disabled 
            ? 'bg-gray-50 cursor-not-allowed border-gray-200 text-gray-400'
            : isOpen
            ? 'border-blue-500 ring-1 ring-blue-500'
            : 'border-gray-300 hover:border-blue-400'
        )}
        onClick={handleToggle}
      >
        <span className={clsx(
          'block truncate',
          !selectedOption && 'text-gray-400'
        )}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        
        <div className="flex items-center space-x-1">
          {allowClear && selectedOption && !disabled && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
          <ChevronDownIcon 
            className={clsx(
              'h-4 w-4 text-gray-400 transition-transform',
              isOpen && 'transform rotate-180'
            )}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          
          {/* Options List */}
          <div className="max-h-60 overflow-auto">
            {isLoading ? (
              <div className="px-3 py-8 text-center text-gray-500">
                <div className="inline-flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Searching dresses...</span>
                </div>
              </div>
            ) : error ? (
              <div className="px-3 py-4 text-center text-red-500 text-sm">
                Error loading dresses. Please try again.
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-8 text-center text-gray-500">
                <MagnifyingGlassIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <div className="text-sm">
                  {searchTerm.trim() ? (
                    <>
                      <p className="font-medium mb-1">No dresses found</p>
                      <p>Try searching with different keywords</p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium mb-1">Start typing to search</p>
                      <p>Search by dress name, barcode, or color</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                {options.map((option) => (
                  <div
                    key={option.value}
                    className={clsx(
                      'flex items-center justify-between px-3 py-3 cursor-pointer border-b border-gray-50 last:border-b-0',
                      option.disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : selectedValue === option.value
                        ? 'bg-blue-50 text-blue-900'
                        : 'hover:bg-gray-50'
                    )}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="block truncate text-sm">{option.label}</span>
                    {selectedValue === option.value && (
                      <CheckIcon className="h-4 w-4 text-blue-600 flex-shrink-0 ml-2" />
                    )}
                  </div>
                ))}
                {options.length === 100 && (
                  <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t">
                    Showing first 100 results. Use search to narrow down options.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
